import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";
import {
	GetCompanyCalendarIntegrationSettingsResponse,
	GetUserCalendarIntegrationStatusResponse,
	InitiateCalendarIntegrationOAuthFlowResponse,
	SaveSelectedCalendarRequest,
	UpdateCompanyCalendarIntegrationSettingsRequest,
} from "@performyard/service-contracts";

import {
	companyCalendarIntegrationSettingsQuery,
	userCalendarIntegrationStatusQuery,
	userCalendarsQuery,
} from "../../queries";
import {
	disconnectCalendarIntegration,
	getCalendarIntegrationOAuthUrl,
	saveSelectedCalendar,
	updateCompanyCalendarIntegrationSettings,
} from "../../services/defs/internal-api";
import { PYUseQueryOptions } from "../StandardQueryArguments";

export const useCompanyCalendarIntegrationSettings = (
	options?: PYUseQueryOptions<GetCompanyCalendarIntegrationSettingsResponse>
) => {
	const query = useQuery({
		...companyCalendarIntegrationSettingsQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useUpdateCompanyCalendarIntegrationSettings = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: UpdateCompanyCalendarIntegrationSettingsRequest) =>
			updateCompanyCalendarIntegrationSettings(data),
		onSuccess: async () => {
			// Invalidate and refetch company calendar integration settings after successful update
			await queryClient.invalidateQueries({ queryKey: ["company-calendar-integration-settings"] });
			toast.success("Calendar Integration Settings Updated");
		},
		onError: (error) => {
			console.error("Error Updating Calendar Integration Settings:", error);
			toast.error("Failed to Update Calendar Integration Settings");
		},
	});
};

export const useCalendarIntegrationOAuthUrl = ({ provider }: { provider: "google" | "outlook" }) => {
	const {
		mutateAsync,
		isPending: isLoading,
		error,
	} = useMutation<InitiateCalendarIntegrationOAuthFlowResponse>({
		mutationFn: () => getCalendarIntegrationOAuthUrl({ provider }),
		onSuccess: (data) => {
			// When we get the auth URL, redirect the user to it
			if (data.authUrl) {
				window.location.href = data.authUrl;
			}
		},
	});

	return {
		initiateCalendarIntegrationAuth: mutateAsync,
		isLoading,
		error,
	};
};

export const useUserCalendarIntegrationStatus = (
	options?: PYUseQueryOptions<GetUserCalendarIntegrationStatusResponse>
) => {
	const query = useQuery({
		...userCalendarIntegrationStatusQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
	};
};

export const useDisconnectCalendarIntegration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			return disconnectCalendarIntegration();
		},
		onSuccess: async () => {
			// Invalidate and refetch user calendar status after successful disconnect
			await queryClient.invalidateQueries({ queryKey: ["user-calendar-integration-status"] });
			toast.success("Calendar Successfully Disconnected");
		},
		onError: (error) => {
			console.error("Error Disconnecting Calendar:", error);
			toast.error("Failed to Disconnect Calendar");
		},
	});
};

/**
 * Unified hook to get user calendars from any provider
 */
export const useUserCalendars = ({
	provider,
	enabled = true,
}: {
	provider: "google" | "outlook";
	enabled?: boolean;
}) => {
	const query = useQuery({
		...userCalendarsQuery(provider),
		enabled,
	});

	const data = query?.error ? { calendars: [] } : query?.data;

	return {
		data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
		error: query?.error,
	};
};

export const useSaveSelectedCalendar = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: SaveSelectedCalendarRequest) => saveSelectedCalendar(data),
		onSuccess: async () => {
			// Invalidate and refetch user calendar status after successful save
			await queryClient.invalidateQueries({ queryKey: ["user-calendar-integration-status"] });
			toast.success("Calendar Successfully Changed");
		},
		onError: (error) => {
			console.error("Error Changing Calendar:", error);
			toast.error("Failed to Change Calendar");
		},
	});
};
